import { zodResolver } from '@hookform/resolvers/zod';
import {
  Bank,
  Briefcase,
  Buildings,
  Coins,
  CurrencyBtc,
  Diamond,
  Drop,
  Lock,
} from '@phosphor-icons/react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  defaultHorizonForType,
  horizonLabel,
  resolveCategoryFromForm,
  resolveFormTypeFromCategory,
  resolveLiquidityLayer,
} from '../../lib/accountsDisplay';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { BottomSheet, BottomSheetContent } from '../common/BottomSheet';
import { InfoTooltip } from '../common/InfoTooltip';
import { NumericInput } from '../common/NumericInput';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  QUICK_NAME_SUGGESTIONS,
  fieldVisibility,
} from './assetCategories';

const SUPPORTED_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'BTC', 'ETH', 'USDT'];

const FORM_TYPES = [
  { id: 'conta', icon: Bank, labelKey: 'conta' },
  { id: 'corretora', icon: Briefcase, labelKey: 'corretora' },
  { id: 'crypto', icon: CurrencyBtc, labelKey: 'crypto' },
  { id: 'ouro', icon: Diamond, labelKey: 'ouro' },
  { id: 'imobiliario', icon: Buildings, labelKey: 'imobiliario' },
  { id: 'outro', icon: Coins, labelKey: 'outro' },
];

const optionalDecimal = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((value) => {
    if (value === '' || value === null || value === undefined) return null;
    return String(value).replace(',', '.');
  });

const CURRENCY_CODE = /^[A-Za-z]+$/;

const FORM_TYPE_IDS = ['conta', 'corretora', 'crypto', 'ouro', 'imobiliario', 'outro'];

const HORIZON_IDS = ['immediate', 'days_1_2', 'days_2_5', 'weeks', 'months', 'years_plus'];

const schema = z.object({
  name: z.string().min(1, pt.accounts.errors.nameRequired),
  formType: z.string().refine((v) => FORM_TYPE_IDS.includes(v), {
    message: 'Seleciona o tipo de ativo.',
  }),
  liquidityLayer: z
    .string()
    .refine((v) => v === 'liquid' || v === 'invested', {
      message: pt.accounts.errors.liquidityRequired,
    }),
  liquidityHorizon: z
    .string()
    .refine((v) => v === '' || HORIZON_IDS.includes(v), {
      message: 'Horizonte de liquidez inválido.',
    })
    .optional()
    .default(''),
  currency: z
    .string()
    .min(1)
    .max(10)
    .transform((v) => v.trim().toUpperCase())
    .refine((v) => CURRENCY_CODE.test(v), {
      message: pt.accounts.errors.currencyLettersOnly,
    })
    .refine((v) => SUPPORTED_CURRENCIES.includes(v), {
      message: pt.accounts.errors.currencyUnsupported,
    }),
  ticker: z
    .string()
    .optional()
    .default('')
    .transform((v) => v.trim().toUpperCase())
    .refine((v) => !v || CURRENCY_CODE.test(v), {
      message: pt.accounts.errors.tickerLettersOnly,
    }),
  quantity: optionalDecimal,
  acquisition_date: z
    .string()
    .optional()
    .default('')
    .transform((value) => value || ''),
  acquisition_value: optionalDecimal,
});

const DEFAULTS = {
  name: '',
  formType: '',
  liquidityLayer: '',
  liquidityHorizon: '',
  currency: 'EUR',
  ticker: '',
  quantity: '',
  acquisition_date: '',
  acquisition_value: '',
};

/** `.pen` → `ds/form/field-group--default`: label + input/controlo + ajuda opcional. */
function FieldLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="type-label text-foreground">
      {children}
    </label>
  );
}

function FieldHelp({ children }) {
  if (!children) return null;
  return <p className="type-body-sm font-normal text-muted-foreground">{children}</p>;
}

function FieldError({ children }) {
  if (!children) return null;
  return <p className="type-body-sm font-normal text-error">{children}</p>;
}

/** `.pen`: pill de "picker" (tipo / camada de liquidez / horizonte) — radius-ds-md ou full, selected = sage-tint + border-2 sage-500 (não seleccionado = border 1px). */
const PICKER_PILL_BASE = 'flex items-center justify-center gap-1.5 font-semibold transition-colors';
const PICKER_PILL_SELECTED = 'border-2 border-sage-500 bg-sage-tint text-sage-on-tint';
const PICKER_PILL_UNSELECTED =
  'border border-input-border text-muted-foreground hover:border-stone-400 dark:hover:border-stone-500';

function LiquidityToggle({ value, onChange, error }) {
  return (
    <div>
      <div className="mb-space-2 flex items-center gap-1.5">
        <span className="type-label text-foreground">{pt.accounts.liquidity.toggleLabel}</span>
        <InfoTooltip text={pt.accounts.liquidity.toggleTooltip} className="h-3.5 w-3.5" />
      </div>
      <div className="flex gap-space-2">
        {[
          { id: 'liquid', icon: Drop, label: pt.accounts.liquidity.available },
          { id: 'invested', icon: Lock, label: pt.accounts.liquidity.invested },
        ].map((option) => {
          const selected = value === option.id;
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={cn(
                PICKER_PILL_BASE,
                'flex-1 rounded-ds-md px-space-3 py-space-2-5 type-body-sm',
                selected ? PICKER_PILL_SELECTED : PICKER_PILL_UNSELECTED,
              )}
            >
              <Icon size={15} className="shrink-0" aria-hidden />
              {option.label}
            </button>
          );
        })}
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
}

const HORIZON_OPTIONS = [
  { id: 'immediate' },
  { id: 'days_1_2' },
  { id: 'days_2_5' },
  { id: 'weeks' },
  { id: 'months' },
  { id: 'years_plus' },
];

function LiquidityHorizonPicker({ value, onChange }) {
  return (
    <div>
      <div className="mb-space-2 flex items-center gap-1.5">
        <span className="type-label text-foreground">{pt.accounts.liquidity.horizonLabel}</span>
        <InfoTooltip text={pt.accounts.liquidity.horizonTooltip} className="h-3.5 w-3.5" />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {HORIZON_OPTIONS.map((option) => {
          const selected = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              className={cn(
                PICKER_PILL_BASE,
                'rounded-ds-full px-space-3 py-1 type-chip',
                selected ? PICKER_PILL_SELECTED : PICKER_PILL_UNSELECTED,
              )}
            >
              {horizonLabel(option.id)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AccountFormSheet({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  isPending,
}) {
  const isEdit = Boolean(initialValues?.id);

  const { register, handleSubmit, control, watch, reset, setValue, getValues, formState } =
    useForm({
      resolver: zodResolver(schema),
      defaultValues: DEFAULTS,
    });

  const formType = watch('formType');
  const liquidityLayer = watch('liquidityLayer');
  const category = resolveCategoryFromForm(formType, liquidityLayer);
  const visibility = useMemo(() => fieldVisibility(category), [category]);
  const quickNames = QUICK_NAME_SUGGESTIONS[category] || [];

  useEffect(() => {
    if (!isOpen) return;
    const editCategory = initialValues?.category || 'bank_account';
    const editFormType = resolveFormTypeFromCategory(editCategory);
    const editLiquidityLayer = resolveLiquidityLayer(editCategory);
    reset({
      ...DEFAULTS,
      ...(initialValues
        ? {
            name: initialValues.name || '',
            formType: editFormType,
            liquidityLayer: editLiquidityLayer,
            liquidityHorizon:
              initialValues.liquidity_horizon ||
              defaultHorizonForType(editFormType, editLiquidityLayer),
            currency: initialValues.currency || 'EUR',
            ticker: initialValues.ticker || '',
            quantity:
              initialValues.quantity !== null && initialValues.quantity !== undefined
                ? String(initialValues.quantity)
                : '',
            acquisition_date: initialValues.acquisition_date || '',
            acquisition_value:
              initialValues.acquisition_value !== null &&
              initialValues.acquisition_value !== undefined
                ? String(initialValues.acquisition_value)
                : '',
          }
        : {}),
    });
  }, [isOpen, initialValues, reset]);

  useEffect(() => {
    if (!isOpen || isEdit) return;
    const cur = String(getValues('currency') || '').toUpperCase();
    if (formType === 'crypto') {
      if (!cur || cur === 'EUR') setValue('currency', 'BTC', { shouldValidate: true });
    }
  }, [formType, isOpen, isEdit, getValues, setValue]);

  useEffect(() => {
    if (!isOpen || isEdit) return;
    if (formType === '' || formType === 'conta') {
      setValue('liquidityLayer', '', { shouldValidate: false });
      setValue('liquidityHorizon', '', { shouldValidate: false });
    } else {
      setValue('liquidityLayer', 'invested', { shouldValidate: false });
      setValue('liquidityHorizon', defaultHorizonForType(formType, 'invested'), {
        shouldValidate: false,
      });
    }
  }, [formType, isOpen, isEdit, setValue]);

  const submit = (values) => {
    const resolvedCategory = resolveCategoryFromForm(values.formType, values.liquidityLayer);
    const visible = fieldVisibility(resolvedCategory);
    const payload = {
      name: values.name.trim(),
      category: resolvedCategory,
      liquidity_horizon: values.liquidityHorizon || '',
      currency: values.currency,
      ticker: visible.ticker ? values.ticker.trim().toUpperCase() : '',
      quantity:
        visible.quantity && values.quantity !== null && values.quantity !== ''
          ? values.quantity
          : null,
      acquisition_date:
        visible.acquisition && values.acquisition_date ? values.acquisition_date : null,
      acquisition_value:
        visible.acquisition && values.acquisition_value !== null && values.acquisition_value !== ''
          ? values.acquisition_value
          : null,
    };
    onSubmit(payload);
  };

  return (
    <BottomSheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <BottomSheetContent
        title={isEdit ? pt.accounts.form.titleEdit : pt.accounts.form.titleCreate}
        footer={
          <div className="flex items-center justify-end gap-space-3">
            <Button
              type="button"
              variant="ghost"
              className="hidden md:inline-flex"
              onClick={onClose}
            >
              {pt.common.cancel}
            </Button>
            <Button
              type="submit"
              form="account-form-sheet"
              className="w-full md:w-auto"
              disabled={isPending}
            >
              {isPending ? pt.common.loading : isEdit ? pt.accounts.form.submit : pt.accounts.form.submitCreate}
            </Button>
          </div>
        }
      >
        <form id="account-form-sheet" className="flex flex-col gap-space-4 pb-space-2" onSubmit={handleSubmit(submit)}>
          <div>
            <p className="mb-space-2 type-label text-foreground">{pt.accounts.form.typeLabel}</p>
            <Controller
              name="formType"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-3 gap-space-2">
                  {FORM_TYPES.map((item) => {
                    const TypeIcon = item.icon;
                    const selected = field.value === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => field.onChange(item.id)}
                        className={cn(
                          PICKER_PILL_BASE,
                          'flex-col rounded-ds-md px-1 py-space-2-5 type-chip',
                          selected ? PICKER_PILL_SELECTED : PICKER_PILL_UNSELECTED,
                        )}
                      >
                        <TypeIcon size={20} className="shrink-0" aria-hidden />
                        {pt.accounts.form.typeLabels[item.labelKey]}
                      </button>
                    );
                  })}
                </div>
              )}
            />
            <FieldError>{formState.errors.formType?.message}</FieldError>
          </div>

          <div className="flex flex-col gap-space-2">
            <FieldLabel htmlFor="account-name">{pt.accounts.form.nameLabel}</FieldLabel>
            <Input
              id="account-name"
              placeholder={pt.accounts.form.namePlaceholder}
              {...register('name')}
            />
            {quickNames.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {quickNames.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      setValue('name', suggestion, { shouldValidate: true });
                      if (formType === 'crypto' && SUPPORTED_CURRENCIES.includes(suggestion)) {
                        setValue('currency', suggestion, { shouldValidate: true });
                      }
                    }}
                    className="rounded-ds-full border border-input-border px-space-2-5 py-0.5 type-chip font-normal text-muted-foreground transition hover:border-sage-400 hover:bg-sage-tint"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            ) : null}
            <FieldError>{formState.errors.name?.message}</FieldError>
          </div>

          <div
            className={cn(
              'flex flex-col gap-space-3',
              visibility.ticker && 'md:flex-row',
            )}
          >
            <div className="flex flex-1 flex-col gap-space-2">
              <FieldLabel htmlFor="account-currency">{pt.accounts.form.currencyLabel}</FieldLabel>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Input
                    id="account-currency"
                    maxLength={10}
                    placeholder="EUR"
                    value={field.value ?? ''}
                    onChange={(event) => {
                      const lettersOnly = event.target.value.replace(/[^a-zA-Z]/g, '');
                      field.onChange(lettersOnly.toUpperCase());
                    }}
                    onBlur={field.onBlur}
                  />
                )}
              />
              <FieldHelp>{pt.accounts.form.currencyHelp}</FieldHelp>
              <FieldError>{formState.errors.currency?.message}</FieldError>
            </div>

            {visibility.ticker ? (
              <div className="flex flex-1 flex-col gap-space-2">
                <FieldLabel htmlFor="account-ticker">{pt.accounts.form.tickerLabel}</FieldLabel>
                <Controller
                  name="ticker"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="account-ticker"
                      maxLength={20}
                      placeholder={pt.accounts.form.tickerPlaceholder}
                      value={field.value ?? ''}
                      onChange={(event) => {
                        const lettersOnly = event.target.value.replace(/[^a-zA-Z]/g, '');
                        field.onChange(lettersOnly.toUpperCase());
                      }}
                      onBlur={field.onBlur}
                    />
                  )}
                />
                <FieldHelp>{pt.accounts.form.tickerHelp}</FieldHelp>
                <FieldError>{formState.errors.ticker?.message}</FieldError>
              </div>
            ) : null}
          </div>

          {visibility.acquisition ? (
            <div className="grid gap-space-3 sm:grid-cols-2">
              <div className="flex flex-col gap-space-2">
                <FieldLabel htmlFor="account-acq-date">{pt.accounts.form.acquisitionDateLabel}</FieldLabel>
                <Input id="account-acq-date" type="date" {...register('acquisition_date')} />
              </div>
              <div className="flex flex-col gap-space-2">
                <FieldLabel htmlFor="account-acq-value">{pt.accounts.form.acquisitionValueLabel}</FieldLabel>
                <Controller
                  name="acquisition_value"
                  control={control}
                  render={({ field }) => (
                    <NumericInput
                      id="account-acq-value"
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value)}
                    />
                  )}
                />
              </div>
            </div>
          ) : null}

          <Controller
            name="liquidityLayer"
            control={control}
            render={({ field }) => (
              <LiquidityToggle
                value={field.value}
                onChange={(v) => {
                  field.onChange(v);
                  setValue('liquidityHorizon', defaultHorizonForType(formType || 'conta', v), {
                    shouldValidate: false,
                  });
                }}
                error={formState.errors.liquidityLayer?.message}
              />
            )}
          />

          <Controller
            name="liquidityHorizon"
            control={control}
            render={({ field }) => (
              <LiquidityHorizonPicker value={field.value} onChange={field.onChange} />
            )}
          />
        </form>
      </BottomSheetContent>
    </BottomSheet>
  );
}
